import { useState, useMemo } from 'react';
import { Link, useParams } from 'react-router';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowLeft, ExternalLink, Pencil } from 'lucide-react';
import { z } from 'zod';
import type { InferResponseType } from 'hono/client';
import { client } from '@/lib/api';
import { updateVendorSchema, type UpdateVendorInput } from '@everplan/shared';
import { mergeCategorySuggestions } from '@/lib/categories';
import { toExternalHref } from '@/lib/url';
import { formatCents } from '@/lib/format';
import { formatDateUTC } from '@/lib/date';
import InlineEdit from '@/components/InlineEdit';
import VendorStatBand from '@/components/vendors/VendorStatBand';
import VendorContactList from '@/components/vendors/VendorContactList';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { CategoryChipInput } from '@/components/CategoryChipInput';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

// Inferred from the backend route — picks up new computed fields automatically.
type VendorDetail = Extract<
  InferResponseType<typeof client.api.vendors[':id']['$get']>,
  { id: string }
>;

// Form input type — z.input captures pre-transform shape
type UpdateVendorFormInput = z.input<typeof updateVendorSchema>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <h2 className="label-caps text-xs font-semibold text-muted-foreground">{children}</h2>
  );
}

// ---------------------------------------------------------------------------
// Edit form (vendor header)
// ---------------------------------------------------------------------------

interface EditFormProps {
  vendor: VendorDetail;
  suggestions: string[];
  onCancel: () => void;
  onSaved: () => void;
}

function VendorEditForm({ vendor, suggestions, onCancel, onSaved }: EditFormProps) {
  const queryClient = useQueryClient();

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<UpdateVendorFormInput, unknown, UpdateVendorInput>({
    resolver: zodResolver(updateVendorSchema),
    defaultValues: {
      name: vendor.name,
      email: vendor.email,
      phone: vendor.phone,
      website: vendor.website,
      notes: vendor.notes,
      categoryNames: vendor.categoryNames,
    },
  });

  const mutation = useMutation({
    mutationFn: async (data: UpdateVendorInput) => {
      const res = await client.api.vendors[':id'].$put({
        param: { id: vendor.id },
        json: data,
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { error?: string }).error ?? 'Failed to save vendor');
      }
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', vendor.id] });
      queryClient.invalidateQueries({ queryKey: ['vendor-category-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['vendors'] });
      onSaved();
    },
  });

  return (
    <form
      onSubmit={handleSubmit((data) => mutation.mutate(data))}
      className="space-y-4 rounded-2xl border border-border bg-card p-5 shadow-ambient"
    >
      <p className="label-caps text-xs text-muted-foreground">Editing vendor</p>

      {/* Name */}
      <div className="space-y-1">
        <Label htmlFor="ve-name">
          <span className="label-caps text-xs text-muted-foreground">Name *</span>
        </Label>
        <Input
          id="ve-name"
          {...register('name')}
          aria-invalid={!!errors.name}
          className="text-lg font-serif font-semibold"
        />
        {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
      </div>

      {/* Categories */}
      <div className="space-y-1.5">
        <span className="label-caps text-xs text-muted-foreground">Categories</span>
        <Controller
          control={control}
          name="categoryNames"
          render={({ field }) => (
            <CategoryChipInput
              value={field.value ?? []}
              onChange={field.onChange}
              suggestions={suggestions}
            />
          )}
        />
      </div>

      {/* Website */}
      <div className="space-y-1">
        <Label htmlFor="ve-website">
          <span className="label-caps text-xs text-muted-foreground">Website</span>
        </Label>
        <Input id="ve-website" type="text" placeholder="example.com" {...register('website')} />
      </div>

      {/* Email + Phone */}
      <div className="grid gap-3 sm:grid-cols-2">
        <div className="space-y-1">
          <Label htmlFor="ve-email">
            <span className="label-caps text-xs text-muted-foreground">Email</span>
          </Label>
          <Input id="ve-email" type="email" placeholder="vendor@example.com" {...register('email')} />
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email.message as string}</p>
          )}
        </div>
        <div className="space-y-1">
          <Label htmlFor="ve-phone">
            <span className="label-caps text-xs text-muted-foreground">Phone</span>
          </Label>
          <Input id="ve-phone" type="tel" placeholder="(555) 123-4567" {...register('phone')} />
        </div>
      </div>

      {/* Notes */}
      <div className="space-y-1">
        <Label htmlFor="ve-notes">
          <span className="label-caps text-xs text-muted-foreground">Notes</span>
        </Label>
        <Textarea id="ve-notes" rows={3} {...register('notes')} />
      </div>

      {mutation.isError && (
        <p className="text-sm text-destructive">
          {mutation.error?.message ?? 'Something went wrong.'}
        </p>
      )}

      <div className="flex gap-2">
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </Button>
        <Button type="button" variant="outline" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

// ---------------------------------------------------------------------------
// VendorDetailPage
// ---------------------------------------------------------------------------

export default function VendorDetailPage() {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const { data: vendor, isLoading, isError } = useQuery({
    queryKey: ['vendor', id],
    queryFn: async () => {
      const res = await client.api.vendors[':id'].$get({ param: { id: id! } });
      if (!res.ok) throw new Error('Failed to fetch vendor');
      return res.json();
    },
    enabled: !!id,
  });

  const { data: categorySuggestions = [] } = useQuery({
    queryKey: ['vendor-category-suggestions'],
    queryFn: async () => {
      const res = await client.api.vendors['category-suggestions'].$get();
      if (!res.ok) throw new Error('Failed to load category suggestions');
      return res.json() as Promise<string[]>;
    },
  });

  const suggestions = useMemo(() => mergeCategorySuggestions(categorySuggestions), [categorySuggestions]);

  // Notes inline-save mutation
  const updateNotesMutation = useMutation({
    mutationFn: async (notes: string) => {
      const res = await client.api.vendors[':id'].$put({
        param: { id: id! },
        json: { notes },
      });
      if (!res.ok) throw new Error('Failed to save notes');
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendor', id] });
    },
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Link
          to="/vendors"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to Vendors
        </Link>
        <p className="display-tight text-lg text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (isError || !vendor) {
    return (
      <div className="space-y-6">
        <Link
          to="/vendors"
          className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="size-4" />
          Back to Vendors
        </Link>
        <Card className="rounded-3xl border-destructive/20 shadow-ambient">
          <CardContent className="pt-6">
            <p className="text-sm text-destructive">
              {isError ? 'Failed to load vendor. Please refresh.' : 'Vendor not found.'}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Resolve category names for display
  const categoryNames = vendor.categoryNames;

  return (
    <div className="space-y-8">
      {/* Back nav */}
      <Link
        to="/vendors"
        className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="size-4" />
        Back to Vendors
      </Link>

      {/* ── Header ──────────────────────────────────────────────────────── */}
      {editing ? (
        <VendorEditForm
          vendor={vendor}
          suggestions={suggestions}
          onCancel={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      ) : (
        <div className="rounded-2xl bg-card px-6 py-5 shadow-ambient">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 space-y-2">
              <h1 className="display-tight font-serif text-3xl font-semibold leading-tight">
                {vendor.name}
              </h1>

              {/* Category chips */}
              {categoryNames.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                  {categoryNames.map((name) => (
                    <Badge key={name} variant="secondary" className="text-xs">
                      {name}
                    </Badge>
                  ))}
                </div>
              )}

              {/* Meta row */}
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                {vendor.website && (
                  <a
                    href={toExternalHref(vendor.website)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1 hover:text-foreground"
                  >
                    <ExternalLink className="size-3.5" />
                    Website
                  </a>
                )}
                {vendor.email && <span>{vendor.email}</span>}
                {vendor.phone && <span>{vendor.phone}</span>}
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              className="shrink-0"
              onClick={() => setEditing(true)}
            >
              <Pencil className="size-3.5" />
              Edit
            </Button>
          </div>
        </div>
      )}

      {/* ── Stat band ───────────────────────────────────────────────────── */}
      <VendorStatBand stats={vendor.stats} />

      {/* ── Two-column body ─────────────────────────────────────────────── */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Left column — spans 2 */}
        <div className="space-y-6 lg:col-span-2">
          {/* Contacts */}
          <Card className="rounded-2xl shadow-ambient">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2">
                <SectionHeading>Contacts</SectionHeading>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <VendorContactList contacts={vendor.contacts} vendorId={vendor.id} />
            </CardContent>
          </Card>

          {/* Notes */}
          <Card className="rounded-2xl shadow-ambient">
            <CardHeader className="pb-3">
              <CardTitle>
                <SectionHeading>Notes</SectionHeading>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="rounded-xl bg-muted/40 px-4 py-3 text-sm italic text-muted-foreground">
                <InlineEdit
                  value={vendor.notes ?? ''}
                  onSave={(v) => updateNotesMutation.mutate(v)}
                  multiline
                  placeholder="Click to add notes about this vendor…"
                  className="text-sm italic"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right column */}
        <div className="space-y-4">
          <Card className="rounded-2xl shadow-ambient">
            <CardContent className="pt-4">
              <Accordion type="single" collapsible defaultValue="working-on">
                {/* Currently working on */}
                <AccordionItem value="working-on">
                  <AccordionTrigger className="text-sm font-medium">
                    Currently working on
                  </AccordionTrigger>
                  <AccordionContent>
                    {vendor.stats.activeEngagements > 0 ? (
                      <p className="text-sm text-muted-foreground">
                        {vendor.stats.activeEngagements} active engagement
                        {vendor.stats.activeEngagements > 1 ? 's' : ''}.
                        {/* TODO: requires extending GET /api/vendors/:id with active
                            engagements list (project name + status) to render per-item rows. */}
                      </p>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        No active engagements.
                      </p>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Past bookings */}
                <AccordionItem value="past-bookings">
                  <AccordionTrigger className="text-sm font-medium">
                    Past bookings
                  </AccordionTrigger>
                  <AccordionContent>
                    {vendor.recentBookings.length === 0 ? (
                      <p className="text-sm text-muted-foreground">No signed contracts yet.</p>
                    ) : (
                      <div className="space-y-2">
                        {vendor.recentBookings.map((booking) => (
                          <div
                            key={booking.contractId}
                            className="flex items-start justify-between gap-2 text-sm"
                          >
                            <div className="min-w-0">
                              <Link
                                to={`/projects/${booking.projectId}`}
                                className="truncate font-medium hover:underline"
                              >
                                {booking.projectName}
                              </Link>
                              <p className="text-xs text-muted-foreground">
                                {formatDateUTC(booking.signedAt)}
                              </p>
                            </div>
                            {booking.totalAmount != null && (
                              <span className="shrink-0 text-xs text-muted-foreground">
                                {formatCents(booking.totalAmount)}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>

                {/* Quote history */}
                <AccordionItem value="quote-history">
                  <AccordionTrigger className="text-sm font-medium">
                    Quote history
                  </AccordionTrigger>
                  <AccordionContent>
                    {vendor.stats.quoteCount === 0 ? (
                      <p className="text-sm text-muted-foreground">No quotes on file.</p>
                    ) : (
                      <div className="space-y-1 text-sm">
                        <p className="text-muted-foreground">
                          {vendor.stats.quoteCount} quote
                          {vendor.stats.quoteCount > 1 ? 's' : ''} on file.
                        </p>
                        {(vendor.stats.quoteMinCents != null ||
                          vendor.stats.quoteMaxCents != null) && (
                          <p className="text-muted-foreground">
                            Range:{' '}
                            <span className="font-medium text-foreground">
                              {vendor.stats.quoteMinCents === vendor.stats.quoteMaxCents
                                ? formatCents(vendor.stats.quoteMinCents)
                                : `${formatCents(vendor.stats.quoteMinCents)} – ${formatCents(vendor.stats.quoteMaxCents)}`}
                            </span>
                          </p>
                        )}
                        {/* TODO: per-row quote detail requires extending GET /api/vendors/:id
                            to include quotes list with project context. */}
                      </div>
                    )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
